/**
 * DEV-ONLY fallback that stops the benign ResizeObserver loop warning from
 * taking over CRA's error overlay.
 *
 * The real layout contributor (the unbounded auto-resizing Prompt textarea in
 * AiImageDialog) is fixed at the source with a maxRows cap. But MUI's internal
 * ResizeObserver usage (Dialog scroll container, Collapse, Select) can still
 * emit this HARMLESS browser warning when content grows — the browser reports a
 * resize-within-a-frame, not an actual error. In production it never surfaces;
 * only CRA's dev overlay escalates it into a full-screen red "Uncaught runtime
 * error".
 *
 * This is intentionally NARROW: it matches ONLY the two exact ResizeObserver
 * loop messages and only in development. It never touches TypeError,
 * ReferenceError, API errors, render failures, unhandled rejections, or
 * production error reporting. Imported first in index.tsx.
 */

/** The two exact, harmless browser messages — nothing else is matched. */
export const RESIZE_OBSERVER_WARNINGS = new Set<string>([
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications.",
]);

/** Pure predicate (exported for testing) — true ONLY for the benign RO warning. */
export function isBenignResizeObserverMessage(message: unknown): boolean {
  return typeof message === "string" && RESIZE_OBSERVER_WARNINGS.has(message);
}

if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
  window.addEventListener(
    "error",
    (event: ErrorEvent) => {
      if (isBenignResizeObserverMessage(event.message)) {
        // Stop the CRA dev overlay's listener from rendering it. Do NOT prevent
        // default for anything else — genuine errors pass straight through.
        event.stopImmediatePropagation();
      }
    },
    true // capture phase → runs before the overlay's bubble-phase listener
  );
}
